import { useState, useEffect, useMemo } from "react";
import { Header } from "./components/Header";
import { DeckBuilderPanel } from "./components/DeckBuilderPanel";
import { AnalyticsPanel } from "./components/AnalyticsPanel";
import { CardProfileModal } from "./components/CardProfileModal";
import { AuthModal } from "./components/AuthModal";
import { AccountSettingsModal } from "./components/AccountSettingsModal";
import { MyDecksModal } from "./components/MyDecksModal";
import { MyEventsModal } from "./components/MyEventsModal";
import { AdminPanel } from "./components/AdminPanel";
import { ModMessageModal } from "./components/ModMessageModal";
import { InboxModal } from "./components/InboxModal";
import { FeedbackModal } from "./components/FeedbackModal";
import { FriendsBubble } from "./components/FriendsBubble";
import { Footer } from "./components/Footer";
import { AmberEmbers } from "./components/AmberEmbers";
import { useWasmAnalysis } from "./hooks/useWasmAnalysis";
import { preloadWasm } from "./lib/wasm-loader";
import { preloadHeatmaps } from "./lib/heatmap-data";

// MTG color palettes for dynamic plan colors
const MTG_PALETTES = {
  W: { shades: ['#fef3c7', '#fde68a', '#fcd34d', '#fbbf24', '#f59e0b'] },
  U: { shades: ['#bae6fd', '#7dd3fc', '#38bdf8', '#0ea5e9', '#0284c7'] },
  B: { shades: ['#d4d4d8', '#a1a1aa', '#71717a', '#52525b', '#3f3f46'] },
  R: { shades: ['#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c'] },
  G: { shades: ['#86efac', '#4ade80', '#22c55e', '#16a34a', '#15803d'] },
  C: { shades: ['#e2e8f0', '#cbd5e1', '#94a3b8', '#64748b', '#475569'] },
};

// Get dynamic plan colors based on deck's color identity
function getDynamicPlanColors(deck: Card[]) {
  const WUBRG = ['W', 'U', 'B', 'R', 'G'] as const;
  const colorSet = new Set<string>();

  deck.forEach(card => {
    card.colors?.forEach(color => {
      if (WUBRG.includes(color as typeof WUBRG[number])) {
        colorSet.add(color);
      }
    });
  });

  const deckColors = WUBRG.filter(c => colorSet.has(c));
  const palettes = deckColors.length > 0
    ? deckColors.map(c => MTG_PALETTES[c])
    : [MTG_PALETTES.C];

  const plans = ['A', 'B', 'C', 'D', 'E'];
  const colors: Record<string, { hex: string; bg: string; text: string; border: string }> = {};

  plans.forEach((plan, i) => {
    const palette = palettes[i % palettes.length];
    const shade = palette.shades[Math.floor(i / palettes.length) % 5];
    colors[plan] = {
      hex: shade,
      bg: `rgba(${parseInt(shade.slice(1, 3), 16)}, ${parseInt(shade.slice(3, 5), 16)}, ${parseInt(shade.slice(5, 7), 16)}, 0.2)`,
      text: shade,
      border: `rgba(${parseInt(shade.slice(1, 3), 16)}, ${parseInt(shade.slice(3, 5), 16)}, ${parseInt(shade.slice(5, 7), 16)}, 0.3)`,
    };
  });

  return colors;
}

export interface Card {
  id: string;
  name: string;
  quantity: number;  // Base quantity (canonical)
  type: string;
  manaCost: string;
  cmc: number;
  colors: string[];
  colorIdentity?: string[];  // Full color identity including mana symbols in text
  imageUrl: string;
  scryfallId: string;
  isLand?: boolean;
  effectiveMV?: number;

  // Legacy ramp fields (backwards compatible)
  rampType?: string;
  rampTurn?: number;
  rampNet?: number;
  cantrip?: number;
  oracleText?: string;  // Oracle text for detecting mana abilities

  // ============================================
  // R1-R6 RAMP PRIMITIVES (New)
  // ============================================

  /** R1-R6 ramp category classification */
  rampCategory?: 'R1' | 'R2' | 'R3' | 'R4' | 'R5' | 'R6' | 'none';

  /** Has a tap-for-mana ability (for EMA calculation) */
  hasManaAbility?: boolean;

  /** Mana produced when tapped (1 for most, 2 for Sol Ring, etc.) */
  manaProduced?: number;

  /** Colors of mana produced: ['G'], ['any'], ['W', 'U'], etc. */
  manaColors?: string[];

  /** Requires tap to produce mana */
  requiresTap?: boolean;

  /** Creatures are gated by summoning sickness (mana dorks) */
  gatedBySummoningSickness?: boolean;

  /** Has haste keyword (bypasses summoning sickness) */
  hasHaste?: boolean;

  /** One-time use (Dark Ritual, Lotus Petal) */
  isSacrifice?: boolean;

  // R1 specific: Extra land plays (Azusa, Exploration)
  extraLandPlays?: number;

  // R3 specific: Search lands from library
  searchType?: 'hand' | 'battlefield_tapped' | 'battlefield_untapped';
  deckThinning?: number;

  // R5 specific: Multipliers (Nyxbloom Ancient)
  manaMultiplier?: number;  // Also used for compounding effects
  appliesToCreaturesOnly?: boolean;

  // R6 specific: Timing modifiers (Amulet of Vigor)
  causesUntappedEntry?: boolean;
  untapsLandsEndStep?: boolean;

  // ============================================
  // C1-C5 DRAW PRIMITIVES (New)
  // ============================================

  /** C1-C5 draw category classification */
  drawCategory?: 'C1' | 'C2' | 'C3' | 'C4' | 'C5' | 'none';

  /** Cards drawn on resolution */
  cardsDrawn?: number;

  /** Repeatable draw (each turn) */
  isRepeatableDraw?: boolean;

  /** Conditional draw (triggered ability) */
  isConditionalDraw?: boolean;

  /** Draw trigger description */
  drawTrigger?: string;

  // Bonus card advantage modifiers
  surveilAmount?: number;
  scryAmount?: number;
  millAmount?: number;
  filterAmount?: number;
  // manaMultiplier moved to R5 section above

  // Rarity: C=Common, U=Uncommon, R=Rare, M=Mythic, B=Basic Land
  rarity?: 'C' | 'U' | 'R' | 'M' | 'B';

  // Double-faced/transform cards (MDFC, Transform, etc.)
  isDoubleFaced?: boolean;
  layout?: string;  // Scryfall layout: 'modal_dfc', 'transform', 'flip', etc.
  backImageUrl?: string;
  backName?: string;
  backType?: string;  // Back face type line (e.g., "Land" for Shatterskull)
  backCmc?: number;   // Back face CMC (usually 0 for land backs)

  // Per-plan per-copy face selection for MDFCs
  // 0 = front face (spell), 1 = back face (often land)
  // e.g., planCopyFace.A = [0, 0, 1, 1] means copies 3-4 use back face in Plan A
  planCopyFace?: {
    A?: number[];
    B?: number[];
    C?: number[];
    D?: number[];
    E?: number[];
  };

  // Whether this DFC is currently showing its back face in base mode
  // When flipped to back: use backImageUrl, backName, and if backType is Land, treat as land
  isFlipped?: boolean;

  // For lands that enter tapped
  entersTapped?: boolean;
  // 'always' = unconditionally tapped, 'conditional' = optional (shocks, fastlands, etc.)
  tapCondition?: 'always' | 'conditional';

  // Display category flag - sideboard cards shown separately, NOT in curve
  isSideboard?: boolean;

  // Per-plan main deck quantities
  // Plan A defaults to: quantity (mainboard) or 0 (sideboard)
  // Plans B-E default to Plan A quantity unless overridden
  planQuantities?: {
    A?: number;
    B?: number;
    C?: number;
    D?: number;
    E?: number;
  };

  // Per-plan per-copy MV assignments
  // Each plan can have an array of MVs, one per copy
  // e.g., planCopyMVs.A = [1, 1, 2, 0] means 4 copies at costs 1, 1, 2, 0
  planCopyMVs?: {
    A?: number[];
    B?: number[];
    C?: number[];
    D?: number[];
    E?: number[];
  };

  // Per-plan per-copy sideboard status
  // Each plan can have an array of booleans indicating if that copy is in sideboard
  // e.g., planCopySideboard.A = [false, false, true, true] means copies 3&4 in sideboard for Plan A
  planCopySideboard?: {
    A?: boolean[];
    B?: boolean[];
    C?: boolean[];
    D?: boolean[];
    E?: boolean[];
  };

  // Per-plan per-copy cost strings (for display and color tracking)
  // e.g., planCopyCosts.A = ['1W', '2', 'BB', 'G'] shows the original cost input
  planCopyCosts?: {
    A?: string[];
    B?: string[];
    C?: string[];
    D?: string[];
    E?: string[];
  };

  // Commander zone - not in main deck, not in curve
  isCommander?: boolean;

  // Partner/Background detection for commander assignment
  hasPartner?: boolean;           // Has "Partner" keyword
  hasPartnerWith?: string;        // Has "Partner with [name]" - stores the partner name
  isBackground?: boolean;         // Is a Background enchantment
  choosesBackground?: boolean;    // Has "Choose a Background"

  // Companion zone
  isCompanion?: boolean;          // Has "Companion" keyword
  companionCondition?: string;    // The companion's deck restriction text

  // Extra cards (planar, schemes, vanguard, conspiracy, etc.)
  isExtraCard?: boolean;          // Not in main/side - goes to Extra section
  extraCardType?: 'plane' | 'phenomenon' | 'scheme' | 'vanguard' | 'conspiracy' | 'other';

  // Legacy fields for backwards compatibility
  plans?: string[];
  planMVs?: Record<string, number>;
  planRequirements?: Record<string, { copiesRequired: number; effectiveMV?: number }>;

  // ============================================
  // PRICING DATA (from Scryfall)
  // ============================================

  /** USD price (non-foil) */
  priceUsd?: number | null;
  /** USD foil price */
  priceUsdFoil?: number | null;
  /** EUR price (non-foil) */
  priceEur?: number | null;
  /** Set name (e.g., "Double Masters") */
  setName?: string;
  /** Set code (e.g., "2xm") */
  setCode?: string;
  /** TCGPlayer purchase URL */
  purchaseUrlTcg?: string;
  /** Cardmarket purchase URL */
  purchaseUrlCardmarket?: string;
  /** Scryfall page URL */
  scryfallUrl?: string;
}

export default function App() {
  const [deckName, setDeckName] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('coast_deckName') || "Untitled Deck";
    }
    return "Untitled Deck";
  });

  const [format, setFormat] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('coast_format') || "Auto";
    }
    return "Auto";
  });

  const [theme, setTheme] = useState<"dark" | "light">(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('coast_theme') as "dark" | "light") || "dark";
    }
    return "dark";
  });

  const [deck, setDeck] = useState<Card[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('coast_deck');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error('Failed to parse saved deck:', e);
        }
      }
    }
    return [];
  });

  const [planTargetTurns, setPlanTargetTurns] = useState<Record<string, number>>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('coast_plan_turns');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error('Failed to parse saved plan turns:', e);
        }
      }
    }
    return { A: 3, B: 3, C: 3, D: 3, E: 3 };
  });

  // Sideboard cards (separate from main deck)
  const [sideboard, setSideboard] = useState<Card[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('coast_sideboard');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error('Failed to parse saved sideboard:', e);
        }
      }
    }
    return [];
  });

  // Plan configurations for sideboard swaps
  // REMOVED: Now using per-card planQuantities instead

  const [selectedCard, setSelectedCard] = useState<Card | null>(null);

  // Global auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showMyDecksModal, setShowMyDecksModal] = useState(false);
  const [showMyEventsModal, setShowMyEventsModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showModMessage, setShowModMessage] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Active plan state (lifted from AnalyticsPanel for Header stats)
  const [activePlan, setActivePlan] = useState<string>('base');

  // Dynamic plan colors based on deck's color identity
  const planColors = useMemo(() => getDynamicPlanColors(deck), [deck]);

  // Extract deck's color identity for audio player filtering
  // Commander color identity takes PRIORITY over mainboard
  // Sideboard doesn't influence deck identity unless a commander is assigned
  const deckColors = useMemo(() => {
    const colorSet = new Set<string>();

    // First check for commanders in sideboard
    const commanders = sideboard.filter(card => card.isCommander);

    if (commanders.length > 0) {
      // Commander mode: use commander's color identity ONLY
      commanders.forEach(commander => {
        // Use colorIdentity (includes mana symbols in text) over colors
        const identity = commander.colorIdentity || commander.colors || [];
        identity.forEach(color => colorSet.add(color));
      });
    } else {
      // No commander: use mainboard colors only (sideboard doesn't influence)
      deck.forEach(card => {
        // Use colorIdentity if available, otherwise use colors
        const identity = card.colorIdentity || card.colors || [];
        identity.forEach(color => colorSet.add(color));
      });
    }

    return Array.from(colorSet);
  }, [deck, sideboard]);

  // Input mode state (shared between DeckBuilder and Analytics)
  const [inputMode, setInputMode] = useState<'search' | 'import'>('search');

  // Helper: Get a card's effective quantity for a given plan
  // Plan A: defaults to quantity (mainboard) or 0 (sideboard)
  // Plans B-E: defaults to Plan A quantity unless overridden
  const getCardPlanQuantity = (card: Card, plan: 'A' | 'B' | 'C' | 'D' | 'E'): number => {
    // If explicitly set for this plan, use it
    if (card.planQuantities?.[plan] !== undefined) {
      return card.planQuantities[plan]!;
    }

    // For Plan A, default to quantity (mainboard) or 0 (sideboard)
    if (plan === 'A') {
      return card.isSideboard ? 0 : card.quantity;
    }

    // For Plans B-E, default to Plan A quantity
    return getCardPlanQuantity(card, 'A');
  };

  // Combine deck and sideboard into single card pool
  const allCards = [...deck, ...sideboard];

  // Calculate land count and total cards
  const landCount = deck.reduce(
    (sum, card) => (card.isLand ? sum + card.quantity : sum),
    0,
  );

  const totalCards = deck.reduce(
    (sum, card) => sum + card.quantity,
    0,
  );

  // Calculate deck stats for header
  const spellCount = deck.filter(c => !c.isLand).reduce((sum, c) => sum + c.quantity, 0);
  const weight = Math.round(deck.filter(c => !c.isLand).reduce((sum, c) => sum + (c.effectiveMV ?? c.cmc) * c.quantity, 0));
  const avgMV = spellCount > 0 ? weight / spellCount : 0;
  const wpl = landCount > 0 ? weight / landCount : 0;

  // Initialize WASM and heatmaps on mount
  useEffect(() => {
    Promise.all([
      preloadWasm(),
      preloadHeatmaps(),
    ]).catch((error) => {
      console.error("Failed to preload resources:", error);
    });

    // Check for deck permalink: ?d=CODE
    const params = new URLSearchParams(window.location.search);
    const deckCode = params.get('d');
    if (deckCode) {
      import('./lib/supabase').then(({ deckService }) => {
        deckService.getDeckByCode(deckCode).then((deckRecord) => {
          if (deckRecord) {
            setFormat(deckRecord.format || 'standard');
            loadDeckFromDiscover(
              deckRecord.cards || {},
              deckRecord.lands || {},
              deckRecord.sideboard || {},
              deckRecord.name || 'Shared Deck'
            );
            // Clean URL
            window.history.replaceState({}, '', window.location.pathname);
          }
        }).catch(() => {
          console.warn('[permalink] Deck code not found:', deckCode);
        });
      });
    }
  }, []);

  // Save deck to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('coast_deck', JSON.stringify(deck));
    }
  }, [deck]);

  // Save deck name to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('coast_deckName', deckName);
    }
  }, [deckName]);

  // Save format to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('coast_format', format);
    }
  }, [format]);

  // Save theme to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('coast_theme', theme);
    }
  }, [theme]);

  // Save plan target turns to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('coast_plan_turns', JSON.stringify(planTargetTurns));
    }
  }, [planTargetTurns]);

  // Save sideboard to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('coast_sideboard', JSON.stringify(sideboard));
    }
  }, [sideboard]);

  // Use WASM analysis hook
  const { analysis, isAnalyzing, error: analysisError } = useWasmAnalysis(
    deck,
    deckName,
    format,
    landCount,
    planTargetTurns,
    {
      debounceMs: 300,
      autoAnalyze: true,
    }
  );

  const updateCard = (
    cardId: string,
    updates: Partial<Card>,
  ) => {
    setDeck((prev) =>
      prev.map((card) => {
        if (card.id !== cardId) return card;

        const updatedCard = { ...card, ...updates };

        // If quantity changed, resize planCopyMVs and planCopySideboard arrays
        if (updates.quantity !== undefined && updates.quantity !== card.quantity) {
          const newQty = updates.quantity;
          const baseMV = card.effectiveMV ?? card.cmc;

          // Resize planCopyMVs
          if (card.planCopyMVs) {
            const resizedMVs: typeof card.planCopyMVs = {};
            (['A', 'B', 'C', 'D', 'E'] as const).forEach(plan => {
              const existing = card.planCopyMVs?.[plan] || [];
              if (newQty > existing.length) {
                // Extend with base MV
                resizedMVs[plan] = [...existing, ...Array(newQty - existing.length).fill(baseMV)];
              } else {
                // Truncate
                resizedMVs[plan] = existing.slice(0, newQty);
              }
            });
            updatedCard.planCopyMVs = resizedMVs;
          }

          // Resize planCopySideboard
          if (card.planCopySideboard) {
            const resizedSB: typeof card.planCopySideboard = {};
            (['A', 'B', 'C', 'D', 'E'] as const).forEach(plan => {
              const existing = card.planCopySideboard?.[plan] || [];
              if (newQty > existing.length) {
                // Extend with false (not in sideboard)
                resizedSB[plan] = [...existing, ...Array(newQty - existing.length).fill(false)];
              } else {
                // Truncate
                resizedSB[plan] = existing.slice(0, newQty);
              }
            });
            updatedCard.planCopySideboard = resizedSB;
          }
        }

        return updatedCard;
      }),
    );
  };

  const removeCard = (cardId: string) => {
    setDeck((prev) =>
      prev.filter((card) => card.id !== cardId),
    );
  };

  const addCard = (card: Card) => {
    const existing = deck.find((c) => c.name === card.name);
    if (existing) {
      updateCard(existing.id, {
        quantity: existing.quantity + card.quantity,
      });
    } else {
      setDeck((prev) => [
        ...prev,
        { ...card, id: card.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}` },
      ]);
    }
  };

  // Sideboard management functions
  const addToSideboard = (card: Card) => {
    const existing = sideboard.find((c) => c.name === card.name);
    if (existing) {
      setSideboard((prev) =>
        prev.map((c) =>
          c.id === existing.id ? { ...c, quantity: c.quantity + card.quantity } : c
        )
      );
    } else {
      setSideboard((prev) => [
        ...prev,
        { ...card, id: card.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, isSideboard: true },
      ]);
    }
  };

  const removeFromSideboard = (cardId: string) => {
    setSideboard((prev) => prev.filter((card) => card.id !== cardId));
  };

  const updateSideboardCard = (cardId: string, updates: Partial<Card>) => {
    setSideboard((prev) =>
      prev.map((card) => {
        if (card.id !== cardId) return card;

        const updatedCard = { ...card, ...updates };

        // If quantity changed, resize planCopyMVs and planCopySideboard arrays
        if (updates.quantity !== undefined && updates.quantity !== card.quantity) {
          const newQty = updates.quantity;
          const baseMV = card.effectiveMV ?? card.cmc;

          if (card.planCopyMVs) {
            const resizedMVs: typeof card.planCopyMVs = {};
            (['A', 'B', 'C', 'D', 'E'] as const).forEach(plan => {
              const existing = card.planCopyMVs?.[plan] || [];
              resizedMVs[plan] = newQty > existing.length
                ? [...existing, ...Array(newQty - existing.length).fill(baseMV)]
                : existing.slice(0, newQty);
            });
            updatedCard.planCopyMVs = resizedMVs;
          }

          if (card.planCopySideboard) {
            const resizedSB: typeof card.planCopySideboard = {};
            (['A', 'B', 'C', 'D', 'E'] as const).forEach(plan => {
              const existing = card.planCopySideboard?.[plan] || [];
              resizedSB[plan] = newQty > existing.length
                ? [...existing, ...Array(newQty - existing.length).fill(true)] // Sideboard cards default to true
                : existing.slice(0, newQty);
            });
            updatedCard.planCopySideboard = resizedSB;
          }
        }

        return updatedCard;
      })
    );
  };

  const moveToSideboard = (cardId: string) => {
    const card = deck.find((c) => c.id === cardId);
    if (card) {
      removeCard(cardId);
      addToSideboard({ ...card, isSideboard: true });
    }
  };

  const moveToMainDeck = (cardId: string) => {
    const card = sideboard.find((c) => c.id === cardId);
    if (card) {
      removeFromSideboard(cardId);
      addCard({ ...card, isSideboard: false });
    }
  };

  // Update a card's quantity for a specific plan (sideboarding per-plan)
  // - Sideboard-only cards: planQuantity = how many copies go TO main for this plan
  // - Mainboard-only cards: planQuantity = how many copies STAY in main for this plan
  // - Cards in both zones: unified pool, redistribute between zones
  const updatePlanQuantity = (
    cardId: string,
    plan: 'A' | 'B' | 'C' | 'D' | 'E',
    mainQuantity: number
  ) => {
    // Check if card is in main deck or sideboard
    const inMain = deck.find((c) => c.id === cardId);
    const inSide = sideboard.find((c) => c.id === cardId);
    const card = inMain || inSide;

    if (!card) return;

    // Find matching cards with same name in both zones
    const mainMatch = deck.find(c => c.name === card.name);
    const sideMatch = sideboard.find(c => c.name === card.name);

    // Case 1: Card exists in BOTH zones - unified pool
    if (mainMatch && sideMatch) {
      const totalPool = mainMatch.quantity + sideMatch.quantity;
      const clampedMain = Math.max(0, Math.min(mainQuantity, totalPool));
      const sideQuantity = totalPool - clampedMain;

      // Update mainboard card's plan quantity
      setDeck((prev) =>
        prev.map((c) =>
          c.id === mainMatch.id
            ? {
              ...c,
              planQuantities: {
                ...c.planQuantities,
                [plan]: clampedMain,
              },
            }
            : c
        )
      );

      // Update sideboard card's plan quantity (how many stay in side)
      setSideboard((prev) =>
        prev.map((c) =>
          c.id === sideMatch.id
            ? {
              ...c,
              planQuantities: {
                ...c.planQuantities,
                [plan]: sideQuantity,
              },
            }
            : c
        )
      );
    }
    // Case 2: Card only in SIDEBOARD - planQuantity = how many go to main
    else if (inSide) {
      setSideboard((prev) =>
        prev.map((c) =>
          c.id === cardId
            ? {
              ...c,
              planQuantities: {
                ...c.planQuantities,
                [plan]: mainQuantity, // How many copies go TO main for this plan
              },
            }
            : c
        )
      );
    }
    // Case 3: Card only in MAINBOARD - planQuantity = how many stay in main
    else if (inMain) {
      setDeck((prev) =>
        prev.map((c) =>
          c.id === cardId
            ? {
              ...c,
              planQuantities: {
                ...c.planQuantities,
                [plan]: mainQuantity, // How many copies STAY in main for this plan
              },
            }
            : c
        )
      );
    }
  };

  // Update a card's MV for a specific plan
  const updatePlanMV = (
    cardId: string,
    plan: 'A' | 'B' | 'C' | 'D' | 'E',
    mv: number
  ) => {
    const inMain = deck.find((c) => c.id === cardId);
    const inSide = sideboard.find((c) => c.id === cardId);

    if (inMain) {
      setDeck((prev) =>
        prev.map((card) =>
          card.id === cardId
            ? {
              ...card,
              planMVs: {
                ...card.planMVs,
                [plan]: mv,
              },
            }
            : card
        )
      );
    } else if (inSide) {
      setSideboard((prev) =>
        prev.map((card) =>
          card.id === cardId
            ? {
              ...card,
              planMVs: {
                ...card.planMVs,
                [plan]: mv,
              },
            }
            : card
        )
      );
    }
  };

  // Update per-copy MVs for a specific plan
  const updatePlanCopyMVs = (
    cardId: string,
    plan: 'A' | 'B' | 'C' | 'D' | 'E',
    mvs: number[]
  ) => {
    const inMain = deck.find((c) => c.id === cardId);
    const inSide = sideboard.find((c) => c.id === cardId);

    if (inMain) {
      setDeck((prev) =>
        prev.map((card) =>
          card.id === cardId
            ? {
              ...card,
              planCopyMVs: {
                ...card.planCopyMVs,
                [plan]: mvs,
              },
            }
            : card
        )
      );
    } else if (inSide) {
      setSideboard((prev) =>
        prev.map((card) =>
          card.id === cardId
            ? {
              ...card,
              planCopyMVs: {
                ...card.planCopyMVs,
                [plan]: mvs,
              },
            }
            : card
        )
      );
    }
  };

  // Update per-copy sideboard status for a specific plan
  const updatePlanCopySideboard = (
    cardId: string,
    plan: 'A' | 'B' | 'C' | 'D' | 'E',
    sbs: boolean[]
  ) => {
    const inMain = deck.find((c) => c.id === cardId);
    const inSide = sideboard.find((c) => c.id === cardId);

    if (inMain) {
      setDeck((prev) =>
        prev.map((card) =>
          card.id === cardId
            ? {
              ...card,
              planCopySideboard: {
                ...card.planCopySideboard,
                [plan]: sbs,
              },
            }
            : card
        )
      );
    } else if (inSide) {
      setSideboard((prev) =>
        prev.map((card) =>
          card.id === cardId
            ? {
              ...card,
              planCopySideboard: {
                ...card.planCopySideboard,
                [plan]: sbs,
              },
            }
            : card
        )
      );
    }
  };

  // Update per-copy face selection for MDFCs for a specific plan
  // 0 = front face (spell), 1 = back face (often land)
  const updatePlanCopyFace = (
    cardId: string,
    plan: 'A' | 'B' | 'C' | 'D' | 'E',
    faces: number[]
  ) => {
    const inMain = deck.find((c) => c.id === cardId);
    const inSide = sideboard.find((c) => c.id === cardId);

    if (inMain) {
      setDeck((prev) =>
        prev.map((card) =>
          card.id === cardId
            ? {
              ...card,
              planCopyFace: {
                ...card.planCopyFace,
                [plan]: faces,
              },
            }
            : card
        )
      );
    } else if (inSide) {
      setSideboard((prev) =>
        prev.map((card) =>
          card.id === cardId
            ? {
              ...card,
              planCopyFace: {
                ...card.planCopyFace,
                [plan]: faces,
              },
            }
            : card
        )
      );
    }
  };

  const clearDeck = () => {
    setDeck([]);
    setSideboard([]);
    setDeckName("Untitled Deck");
    setFormat("Auto");
  };

  // Load a deck from the Discover tab
  const loadDeckFromDiscover = async (
    cards: Record<string, number>,
    lands: Record<string, number>,
    sideboardCards: Record<string, number>,
    name: string
  ) => {
    // Clear current deck
    setDeck([]);
    setSideboard([]);
    setDeckName(name);

    // Helper to fetch card data from Scryfall
    const fetchCard = async (cardName: string, quantity: number, isSideboard = false, isLandHint = false): Promise<Card | null> => {
      try {
        const response = await fetch(`https://api.scryfall.com/cards/named?fuzzy=${encodeURIComponent(cardName)}`);
        if (!response.ok) return null;
        const data = await response.json();

        return {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: data.name,
          quantity,
          type: data.type_line || '',
          // For adventure/split cards, data.mana_cost is combined (e.g. "{6}{G} // {2}{G}") - use front face only
          manaCost: (data.layout === 'adventure' || data.layout === 'split')
            ? (data.card_faces?.[0]?.mana_cost || data.mana_cost || '')
            : (data.mana_cost || data.card_faces?.[0]?.mana_cost || ''),
          cmc: data.cmc || 0,
          colors: data.colors || [],
          colorIdentity: data.color_identity || [],
          imageUrl: data.image_uris?.normal || data.card_faces?.[0]?.image_uris?.normal || '',
          scryfallId: data.id,
          isLand: isLandHint || data.type_line?.toLowerCase().includes('land'),
          isSideboard,
          rarity: data.rarity?.[0]?.toUpperCase() as Card['rarity'],
          isDoubleFaced: !!data.card_faces,
          layout: data.layout,
          backImageUrl: data.card_faces?.[1]?.image_uris?.normal,
          backName: data.card_faces?.[1]?.name,
          backType: data.card_faces?.[1]?.type_line,
        };
      } catch {
        return null;
      }
    };

    // Collect all card fetches
    const mainDeckPromises: Promise<Card | null>[] = [];
    const landPromises: Promise<Card | null>[] = [];
    const sideboardPromises: Promise<Card | null>[] = [];

    // Queue main deck cards
    for (const [name, qty] of Object.entries(cards)) {
      mainDeckPromises.push(fetchCard(name, qty, false, false));
    }

    // Queue lands
    for (const [name, qty] of Object.entries(lands)) {
      landPromises.push(fetchCard(name, qty, false, true));
    }

    // Queue sideboard
    for (const [name, qty] of Object.entries(sideboardCards)) {
      sideboardPromises.push(fetchCard(name, qty, true, false));
    }

    // Wait for all fetches (with rate limiting awareness)
    const [mainCards, landCards, sideCards] = await Promise.all([
      Promise.all(mainDeckPromises),
      Promise.all(landPromises),
      Promise.all(sideboardPromises)
    ]);

    // Filter out nulls and add to deck
    const validMainCards = mainCards.filter((c): c is Card => c !== null);
    const validLandCards = landCards.filter((c): c is Card => c !== null);
    const validSideCards = sideCards.filter((c): c is Card => c !== null);

    setDeck([...validMainCards, ...validLandCards]);
    setSideboard(validSideCards);

    alert(`Loaded "${name}" with ${validMainCards.length + validLandCards.length} cards and ${validSideCards.length} sideboard cards!`);
  };

  const clearStorage = () => {
    localStorage.clear();
    window.location.reload();
  };

  // Make a card the commander (moves to sideboard with isCommander flag)
  const makeCommander = (card: Card) => {
    // Remove from deck
    setDeck(prev => prev.filter(c => c.id !== card.id));

    // Add to sideboard as commander
    const commanderCard: Card = {
      ...card,
      isCommander: true,
      isSideboard: true,
      quantity: 1, // Commanders are always 1 copy
    };

    setSideboard(prev => {
      // Remove any existing commander (can only have one or two for partners)
      // For now, just add - could check for partner later
      return [commanderCard, ...prev];
    });
  };

  // Make a card the companion (moves to sideboard with isCompanion flag)
  const makeCompanion = (card: Card) => {
    // Remove from deck
    setDeck(prev => prev.filter(c => c.id !== card.id));
    // Also remove from sideboard if it's already there
    setSideboard(prev => prev.filter(c => c.id !== card.id));

    // Add to sideboard as companion
    const companionCard: Card = {
      ...card,
      isCompanion: true,
      isSideboard: true,
      quantity: 1, // Companions are always 1 copy
    };

    setSideboard(prev => {
      // Remove any existing companion (only one companion allowed)
      const withoutCompanion = prev.filter(c => !c.isCompanion);
      return [companionCard, ...withoutCompanion];
    });
  };

  return (
    <div className={`h-screen overflow-hidden flex flex-col ${theme === 'dark' ? 'dark bg-[#020617]' : 'bg-white'}`}>
      {/* Ambient amber ember particles */}
      {theme === 'dark' && <AmberEmbers />}
      <Header
        deckColors={deckColors}
        onResetSession={clearStorage}
        onOpenAuth={() => setShowAuthModal(true)}
        onOpenSettings={() => setShowSettingsModal(true)}
        onOpenMyDecks={() => setShowMyDecksModal(true)}
        onOpenMyEvents={() => setShowMyEventsModal(true)}
        onOpenAdminPanel={() => setShowAdminPanel(true)}
        onOpenModMessage={() => setShowModMessage(true)}
        onOpenInbox={() => setShowInbox(true)}
        onOpenFeedback={() => setShowFeedback(true)}
        onNavigateToLanding={() => {
          if (window.location.pathname !== '/') {
            window.history.pushState(null, '', '/');
            window.dispatchEvent(new Event('popstate'));
          }
        }}
      />

      {/* Main content - full width now that Analysis is consolidated into Curve tab */}
      <div className="flex-1 p-1 sm:p-2 pb-16 lg:pb-2 overflow-hidden">
        {/* Main Panel - Build + Analytics (now includes Overview in Curve tab) */}
        <div className="overflow-hidden min-w-0 h-full">
          <AnalyticsPanel
            deck={deck}
            sideboard={sideboard}
            landCount={landCount}
            totalCards={totalCards}
            analysis={analysis}
            isAnalyzing={isAnalyzing}
            inputMode={inputMode}
            deckName={deckName}
            format={format}
            activePlan={activePlan}
            onActivePlanChange={setActivePlan}
            onCardClick={setSelectedCard}
            onUpdateCard={updateCard}
            onRemoveCard={removeCard}
            onAddCard={addCard}
            onAddToSideboard={addToSideboard}
            onInputModeChange={setInputMode}
            onUpdatePlanQuantity={updatePlanQuantity}
            onUpdatePlanMV={updatePlanMV}
            onUpdatePlanCopyMVs={updatePlanCopyMVs}
            onUpdatePlanCopySideboard={updatePlanCopySideboard}
            onUpdatePlanCopyFace={updatePlanCopyFace}
            onMoveToSideboard={moveToSideboard}
            onMoveToMainDeck={moveToMainDeck}
            onUpdateSideboardCard={updateSideboardCard}
            onRemoveFromSideboard={removeFromSideboard}
            onFormatDetect={setFormat}
            onClearDeck={clearDeck}
            planTargetTurns={planTargetTurns}
            onUpdatePlanTargetTurns={setPlanTargetTurns}
            onLoadDeckFromDiscover={loadDeckFromDiscover}
            onOpenAuth={() => setShowAuthModal(true)}
          />
        </div>
      </div>

      {/* Analysis Error Display */}
      {analysisError && (
        <div className="fixed bottom-4 right-4 bg-red-500/90 text-white px-4 py-3 rounded-lg shadow-lg max-w-md">
          <div className="font-semibold">Analysis Error</div>
          <div className="text-sm">{analysisError}</div>
        </div>
      )}

      {selectedCard && (
        <CardProfileModal
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
          onSave={(updates) => {
            updateCard(selectedCard.id, updates);
            // Don't close here - let the modal show "Close?" prompt
          }}
          onUpdateQuantity={(qty) => {
            updateCard(selectedCard.id, { quantity: qty });
            // Update selectedCard state to reflect the new quantity
            setSelectedCard(prev => prev ? { ...prev, quantity: qty } : null);
          }}
          onMakeCommander={makeCommander}
          onMakeCompanion={makeCompanion}
          planColors={planColors}
          deckSize={totalCards}
          landCount={landCount}
          activeMatch={activePlan}
        />
      )}

      {/* Global Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

      {/* Account Settings Modal */}
      <AccountSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      {/* My Decks Modal */}
      <MyDecksModal
        isOpen={showMyDecksModal}
        onClose={() => setShowMyDecksModal(false)}
        onLoadDeck={(deckRecord) => {
          // Parse the deck cards and load into state
          if (deckRecord.cards && Array.isArray(deckRecord.cards)) {
            setDeck(deckRecord.cards);
          }
          if (deckRecord.sideboard && Array.isArray(deckRecord.sideboard)) {
            setSideboard(deckRecord.sideboard);
          }
          if (deckRecord.name) {
            setDeckName(deckRecord.name);
          }
          if (deckRecord.format) {
            setFormat(deckRecord.format);
          }
        }}
      />

      {/* Admin Panel */}
      <AdminPanel
        isOpen={showAdminPanel}
        onClose={() => setShowAdminPanel(false)}
      />

      {/* Mod Message Modal */}
      <ModMessageModal
        isOpen={showModMessage}
        onClose={() => setShowModMessage(false)}
      />

      {/* User Inbox Modal */}
      <InboxModal
        isOpen={showInbox}
        onClose={() => setShowInbox(false)}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
      />

      {/* My Events Modal */}
      <MyEventsModal
        isOpen={showMyEventsModal}
        onClose={() => setShowMyEventsModal(false)}
      />

      {/* Friends Bubble — fixed bottom-right social widget */}
      <FriendsBubble />

      {/* Collapsible Footer with DECKSTR branding */}
      <Footer onResetSession={clearStorage} deckColors={deckColors} />
    </div>
  );
}